import React, { useEffect, useRef } from "react";

interface TetherConfig {
  panelId: string;
  toolbarId: string;
}

interface TetherLinesProps {
  /** Map of panel-id → toolbar-button-id for each open panel */
  configs: TetherConfig[];
  openPanelIds: string[];
  strokeColor?: string;
  dotColor?: string;
}

/**
 * Draws animated bezier tether lines from each visible floating panel's
 * bottom-center point to its corresponding toolbar button.
 *
 * Uses a requestAnimationFrame loop and direct DOM mutation so it never
 * triggers React re-renders during dragging.
 */
export const TetherLines: React.FC<TetherLinesProps> = ({
  configs,
  openPanelIds,
  strokeColor = "rgba(124,58,237,.3)",
  dotColor = "rgba(124,58,237,.6)",
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (openPanelIds.length === 0) {
      // Clear SVG when nothing is open
      if (svgRef.current) svgRef.current.innerHTML = "";
      return;
    }

    let rafId: number;

    function draw() {
      const svg = svgRef.current;
      if (!svg) return;

      // Clear previous frame
      while (svg.firstChild) svg.removeChild(svg.firstChild);

      configs.forEach(({ panelId, toolbarId }) => {
        if (!openPanelIds.includes(panelId)) return;

        const panel = document.querySelector(`[data-panel-id="${panelId}"]`);
        const btn = document.querySelector(`[data-toolbar-id="${toolbarId}"]`);
        if (!panel || !btn) return;

        const pr = panel.getBoundingClientRect();
        const tr = btn.getBoundingClientRect();

        // Panel anchor: bottom-center
        const px = pr.left + pr.width / 2;
        const py = pr.bottom;
        // Toolbar anchor: top-center
        const tx = tr.left + tr.width / 2;
        const ty = tr.top;

        // Don't draw if panel is directly above the toolbar (very short tether)
        if (Math.abs(py - ty) < 20) return;

        const cy = (py + ty) / 2;

        const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path.setAttribute("d", `M ${px} ${py} C ${px} ${cy + 24}, ${tx} ${cy - 24}, ${tx} ${ty}`);
        path.setAttribute("stroke", strokeColor);
        path.setAttribute("stroke-width", "1.5");
        path.setAttribute("fill", "none");
        path.setAttribute("stroke-dasharray", "4 4");
        svg.appendChild(path);

        // Anchor dot at panel end
        const dot = document.createElementNS("http://www.w3.org/2000/svg", "circle");
        dot.setAttribute("cx", String(px));
        dot.setAttribute("cy", String(py));
        dot.setAttribute("r", "3");
        dot.setAttribute("fill", dotColor);
        svg.appendChild(dot);
      });

      rafId = requestAnimationFrame(draw);
    }

    rafId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafId);
  }, [openPanelIds, configs, strokeColor, dotColor]);

  return (
    <svg
      ref={svgRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 49,
        overflow: "visible",
      }}
      xmlns="http://www.w3.org/2000/svg"
    />
  );
};
